/**
 * Normalizes PAMENG-1720 finance report payloads into UI-friendly shapes.
 * Handles common field aliases until the contract is finalized.
 */

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const int = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

function mapSummaryFromSource(summarySource) {
  if (!summarySource || typeof summarySource !== "object") return null;
  return {
    rounds: int(summarySource.total_rounds ?? summarySource.rounds),
    players: int(summarySource.total_players ?? summarySource.players),
    turnover: num(summarySource.turnover),
    wins: num(summarySource.wins),
    ggr: num(summarySource.ggr ?? summarySource.total_ggr),
  };
}

function hasSummaryFields(source) {
  if (!source || typeof source !== "object") return false;
  return (
    source.total_rounds != null ||
    source.rounds != null ||
    source.total_players != null ||
    source.players != null ||
    source.turnover != null ||
    source.wins != null ||
    source.ggr != null ||
    source.total_ggr != null
  );
}

export function mapFinanceRow(r, index) {
  const turnover = num(r.turnover ?? r.bets_amount ?? r.turnover_amount);
  const wins = num(r.wins ?? r.win_amount);
  const rounds = int(r.rounds);
  const betsCount = int(r.bets_count ?? r.bet_count ?? r.number_of_bets);
  const rtpRaw = r.rtp_pct ?? r.rtp;
  const rtpPct =
    rtpRaw != null && Number.isFinite(Number(rtpRaw))
      ? Number(rtpRaw)
      : turnover > 0
        ? (wins / turnover) * 100
        : 0;
  const avgBetRaw = r.avg_bet ?? r.avg_bet_amount;
  const avgBet =
    avgBetRaw != null && Number.isFinite(Number(avgBetRaw))
      ? Number(avgBetRaw)
      : betsCount > 0
        ? turnover / betsCount
        : 0;
  const ggrRaw = r.ggr;
  const ggr =
    ggrRaw != null && Number.isFinite(Number(ggrRaw))
      ? Number(ggrRaw)
      : turnover - wins;

  const dateKey = r.date ?? r.day ?? r.report_date ?? `row-${index}`;

  return {
    key: String(dateKey),
    date: r.date ?? r.day ?? r.report_date,
    totalGames: int(r.total_games ?? r.totalGames),
    activeGames: int(r.active_games ?? r.activeGames),
    players: int(r.unique_players ?? r.players),
    rounds,
    betsCount,
    turnover,
    wins,
    rtpPct,
    avgBet,
    ggr,
  };
}

export function normalizeCasinoFinanceResponse(raw) {
  if (!raw || typeof raw !== "object") {
    return {
      summary: null,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      nextCursor: null,
      filters: [],
    };
  }

  if (Array.isArray(raw)) {
    const rows = raw.map(mapFinanceRow);
    return {
      summary: null,
      rows,
      total: rows.length,
      page: 1,
      pageSize: rows.length || 20,
      nextCursor: null,
      filters: [],
    };
  }

  const body = raw.data && typeof raw.data === "object" ? raw.data : raw;
  const filters = normalizeFilterListFromEnvelope(raw);

  if (body && typeof body === "object" && Array.isArray(body.items)) {
    const rows = body.items.map(mapFinanceRow);
    const summarySource =
      body.summary ?? body.totals ?? body.kpis ?? body.aggregate ?? null;
    const pagination = body.pagination ?? body.meta ?? {};
    const total = int(
      body.total ??
        pagination.total ??
        pagination.total_results ??
        rows.length,
    );
    const page = int(pagination.page ?? pagination.current_page ?? body.page ?? 1);
    const pageSize = int(
      pagination.page_size ??
        pagination.pageSize ??
        body.page_size ??
        (rows.length || 20),
    );
    const nextCursor = body.next_cursor ?? body.nextCursor ?? null;
    return {
      summary: mapSummaryFromSource(summarySource),
      rows,
      total: total || rows.length,
      page: page || 1,
      pageSize: pageSize || 20,
      nextCursor: nextCursor || null,
      filters,
    };
  }

  if (Array.isArray(body)) {
    const rows = body.map(mapFinanceRow);
    return {
      summary: null,
      rows,
      total: rows.length,
      page: 1,
      pageSize: rows.length || 20,
      nextCursor: null,
      filters,
    };
  }

  const summarySource =
    body.summary ?? body.totals ?? body.kpis ?? body.aggregate ?? null;

  const rowsRaw = body.rows ?? body.data ?? body.results ?? [];
  const rows = Array.isArray(rowsRaw) ? rowsRaw.map(mapFinanceRow) : [];

  const pagination = body.pagination ?? body.meta ?? {};
  const total = int(
    pagination.total ?? pagination.total_results ?? body.total ?? rows.length,
  );
  const page = int(pagination.page ?? pagination.current_page ?? body.page ?? 1);
  const pageSize = int(
    pagination.page_size ?? pagination.pageSize ?? body.page_size ?? 20,
  );

  const summary = mapSummaryFromSource(summarySource);
  const nextCursor = body.next_cursor ?? body.nextCursor ?? null;

  return {
    summary,
    rows,
    total,
    page: page || 1,
    pageSize: pageSize || 20,
    nextCursor: nextCursor || null,
    filters,
  };
}

function mapKpiFilterItem(f) {
  if (!f || typeof f !== "object") return null;
  return {
    key: String(f.key ?? ""),
    multiSelect: Boolean(f.multi_select),
    options: Array.isArray(f.options) ? f.options.map((x) => String(x)) : [],
    defaultValues: Array.isArray(f.default_values)
      ? f.default_values.map((x) => String(x))
      : [],
    selectedValues: Array.isArray(f.selected_values)
      ? f.selected_values.map((x) => String(x))
      : [],
  };
}

/**
 * Reads `filters` from root `data`, or `meta.filters` (summary/KPI envelopes often nest here).
 * `raw.filters` and `body.filters` are only added once when they are the same reference (no `data` wrapper).
 * Prefers the first non-empty array; otherwise the first defined array (may be empty).
 */
function normalizeFilterListFromEnvelope(raw) {
  if (!raw || typeof raw !== "object") return [];
  const body = raw.data && typeof raw.data === "object" ? raw.data : raw;

  const candidateArrays = [];
  const addUnique = (arr) => {
    if (Array.isArray(arr) && !candidateArrays.includes(arr)) {
      candidateArrays.push(arr);
    }
  };
  addUnique(raw.filters);
  addUnique(body?.filters);
  if (body?.meta && typeof body.meta === "object") {
    addUnique(body.meta.filters);
  }
  if (raw.meta && typeof raw.meta === "object") {
    addUnique(raw.meta.filters);
  }

  let chosen = [];
  for (const arr of candidateArrays) {
    if (arr.length > 0) {
      chosen = arr;
      break;
    }
  }
  if (!chosen.length) {
    for (const arr of candidateArrays) {
      chosen = arr;
      break;
    }
  }
  return chosen.map(mapKpiFilterItem).filter(Boolean);
}

/**
 * Normalizes GET /admin-panel-v2/casino/analytics/summary/kpis payloads: KPI totals + filter metadata.
 * Supports e.g. `{ data: { rounds, ... }, filters: [...] }` or legacy `kpis` / nested `data` shapes.
 */
export function normalizeCasinoFinanceKpisResponse(raw) {
  if (!raw || typeof raw !== "object") {
    return { summary: null, filters: [] };
  }

  const filters = normalizeFilterListFromEnvelope(raw);

  const body = raw.data && typeof raw.data === "object" ? raw.data : raw;
  const candidates = [
    body.summary,
    body.kpis,
    body.totals,
    body.aggregate,
    hasSummaryFields(body) ? body : null,
  ];
  const source = candidates.find((c) => c && hasSummaryFields(c));
  return {
    summary: source ? mapSummaryFromSource(source) : null,
    filters,
  };
}
